import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { Layout, Card, Typography, Input, DatePicker, Table, Space, Button } from 'antd';
import {
  StarOutlined, FileExcelOutlined
} from '@ant-design/icons';

import withRoleAccess from '../../hoc/withRoleAccess';
import './Registrations.css';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const TeacherRatingReport = () => {
  const [teacherRatings, setTeacherRatings] = useState([]);
  const [filteredTeacherRatings, setFilteredTeacherRatings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(150);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid date';
    return date.toLocaleDateString(t('locale'), { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
  };

  useEffect(() => {
    const fetchTeacherRatings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/ratings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTeacherRatings(response.data);
      } catch (error) {
        console.error('Error fetching teacher ratings:', error);
      }
    };
    fetchTeacherRatings();
  }, [API_URL]);

  useEffect(() => {
    let filtered = teacherRatings;

    if (searchTerm) {
      filtered = filtered.filter((rating) =>
        rating.teacher_nickname.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (fromDate) {
      filtered = filtered.filter((rating) => new Date(rating.date) >= new Date(fromDate));
    }
    if (toDate) {
      filtered = filtered.filter((rating) => new Date(rating.date) <= new Date(toDate));
    }

    setFilteredTeacherRatings(filtered);
    setCurrentPage(1);
  }, [searchTerm, fromDate, toDate, teacherRatings]);

  const currentItems = filteredTeacherRatings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const columns = [
    { title: t('Teacher'), dataIndex: 'teacher_nickname', key: 'teacher_nickname' },
    { title: t('Class Code'), dataIndex: 'class_code', key: 'class_code' },
    { title: t('Subject'), dataIndex: 'subject_name', key: 'subject_name' },
    {
      title: t('Date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date),
    },
    { title: t('Student'), dataIndex: 'student_nickname', key: 'student_nickname' },
    { title: t('Rating'), dataIndex: 'teacher_rating', key: 'teacher_rating' },
    { title: t('Comments'), dataIndex: 'studentcomment', key: 'studentcomment' },
    {
      title: t('Timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => formatDate(timestamp),
    },
  ];

  const handleDateRangeChange = (dates) => {
    setFromDate(dates ? dates[0].toDate() : null);
    setToDate(dates ? dates[1].toDate() : null);
  };

  return (
    <Card>
        
          <Title level={3}>
            <StarOutlined /> Teacher Rating
          </Title>
      <Layout>
        <Content>

          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder={t('Search by Teacher Name')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
            />
            <RangePicker onChange={handleDateRangeChange} />
            <CSVLink data={currentItems} filename="teacher_rating_report.csv" className="ant-btn">
              <Button><FileExcelOutlined style={{ marginRight: 6 }} /> {t('Export CSV')}</Button>
            </CSVLink>
          </Space>

          <Table
            columns={columns}
            dataSource={currentItems}
              pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        }}
            rowKey={(record, index) => index}
            scroll={{ 
              x: 1500, // Set a minimum scroll width
            }}
            style={{
              overflowX: 'auto',
              width: '100%'
            }}
          />
     
        </Content>
      </Layout>
    </Card>
  );
};

export default withRoleAccess(TeacherRatingReport, ['admin', 'superadmin']);
